// Usage events the widget beacons to POST /premises/:slug/events — the replacement for the old
// Lightsail access logs, where every view and click showed up as its own PHP request
// (/v2-1/… = view, /v2/seemore = "see more", /v2/weekchartfetch = Week tab, /v2/updatept =
// AI-PT submit, …). The new widget renders those from data already in memory, so nothing
// reaches the server unless it's sent explicitly. Browser-safe: no Node or DB imports here.

export const WIDGET_EVENT_NAMES = [
  /** The render payload arrived and the widget drew. detail: "full" | "onlytotal". */
  "view",
  /** The compact (data-type="onlytotal") bar's "see more" modal was opened. Named after the
   *  legacy stats event_type (the old /v2/seemore request), so it rolls up unrenamed. */
  "seemore",
  /** A tab was switched to. detail: "now" | "week". */
  "tab",
  /** A day was picked on the Week tab. detail: ISO weekday "1".."7". */
  "weekday",
  /** The AI-PT overlay was opened. */
  "aipt_open",
  /** The AI-PT question form was submitted. Named after the legacy stats event_type (the old
   *  /v2/updatept request), so it rolls up unrenamed. */
  "updatept",
  /** A sibling premise in the chain panel was clicked. detail: that premise's slug. */
  "chain_click",
  /** The "i" help overlay was switched on. */
  "help",
  /** An outbound footer link was clicked. detail: "gymplus" | "privacy". */
  "link",
] as const;

export type WidgetEventName = (typeof WIDGET_EVENT_NAMES)[number];

export interface WidgetEventPayload {
  event: WidgetEventName;
  detail: string | null;
  /** One random UUID per widget load, so events can be grouped into a visit. Not persisted in
   *  the browser — a reload is a new session — so no cookie/storage consent is involved. */
  sessionId: string;
  lang: "fi" | "en" | "sv";
}

const EVENT_NAMES = new Set<string>(WIDGET_EVENT_NAMES);
const LANGS = new Set(["fi", "en", "sv"]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Every detail value the widget sends is a short token (tab name, weekday, slug); anything
// else is a client we didn't write and isn't worth storing.
const DETAIL = /^[a-z0-9_-]{1,64}$/i;

/** Validates an untrusted beacon body. Returns null for anything the widget wouldn't send. */
export function parseWidgetEvent(raw: unknown): WidgetEventPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const { event, detail, sessionId, lang } = raw as Record<string, unknown>;
  if (typeof event !== "string" || !EVENT_NAMES.has(event)) return null;
  if (typeof sessionId !== "string" || !UUID.test(sessionId)) return null;
  if (typeof lang !== "string" || !LANGS.has(lang)) return null;
  if (detail !== undefined && detail !== null && (typeof detail !== "string" || !DETAIL.test(detail))) return null;
  return {
    event: event as WidgetEventName,
    detail: typeof detail === "string" ? detail : null,
    sessionId: sessionId.toLowerCase(),
    lang: lang as WidgetEventPayload["lang"],
  };
}

/**
 * Renames for the events whose legacy `gym.premise_id_gpa_stats.event_type` differs from the
 * new event name (the values the old version's log loader wrote, and which `query_gpa_stats` in
 * ii-gp-agentcore-ai-assistants buckets into "views" and "interactions"). `detail: null` matches
 * any detail; first match wins. Every other event rolls up under its own name — `seemore` and
 * `updatept` already are the legacy names, and the new-only events (aipt_open, weekday,
 * chain_click, help, link) become new event_type values, which query_gpa_stats counts as
 * "other" and leaves out. Their detail (which weekday, which link …) isn't kept in the stats
 * table. The old `update` (60s poll) type isn't tracked at all.
 */
export const LEGACY_STATS_EVENT_TYPES: ReadonlyArray<{
  event: WidgetEventName;
  detail: string | null;
  legacyType: string;
}> = [
  { event: "view", detail: "onlytotal", legacyType: "onlytotal" },
  { event: "view", detail: null, legacyType: "view" },
  { event: "tab", detail: "week", legacyType: "weekchartfetch" },
  // Predictions live on the Now tab in the new widget — the closest match for the old
  // prediction tab's fetch.
  { event: "tab", detail: "now", legacyType: "predictionfetch" },
];

/** The stats event_type an event rolls up into: a legacy rename, or else the event's own name. */
export function legacyStatsEventType(event: WidgetEventName, detail: string | null): string {
  const match = LEGACY_STATS_EVENT_TYPES.find(
    (mapping) => mapping.event === event && (mapping.detail === null || mapping.detail === detail),
  );
  return match?.legacyType ?? event;
}
